import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { RowSelectedEvent } from '@ag-grid-community/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow, RowDataChangeEvent } from '@app/core/components/grid/grid.types';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { RequisitionService, RequisitionSimplifiedGet } from '@app/services/requisition.service';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-requisition-simplified',
    templateUrl: './requisition-simplified.component.html',
    styleUrls: ['./requisition-simplified.component.css'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequisitionSimplifiedComponent implements OnInit, OnDestroy {
    public get?: RequisitionSimplifiedGet;

    public requisitionDelGrid?: GridOptions<RequisitionSimplifiedDelRow>;

    public requisitionGrid?: GridOptions<GridRow>;

    private readonly destroy$ = new Subject<void>();

    private readonly selectedRequisitionDel$ = new Subject<string | number | null>();

    constructor(
        private requisition: RequisitionService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);

        this.selectedRequisitionDel$.pipe(debounceTime(50), takeUntil(this.destroy$)).subscribe((requisitiondelKeyno) => {
            if (!this.get) return;

            this.get.selected_requisitiondel_keyno = requisitiondelKeyno;

            if (requisitiondelKeyno === null) {
                this.requisitionGrid = undefined;
                this.cdr.markForCheck();
                return;
            }

            this.readRequisitionGrid();
            this.cdr.markForCheck();
        });
    }

    public onRequisitionDelGridRead() {
        const gridfunc = this.requisitionDelGrid?.gridfunc;

        if ((gridfunc?.getRows().length ?? 0) === 0) {
            this.selectedRequisitionDel$.next(null);
            return;
        }

        if ((gridfunc?.getGridApi().getSelectedRows().length ?? 0) === 0) gridfunc?.selectRow(0);
    }

    public onRequisitionDelSelected(event: RowSelectedEvent<RequisitionSimplifiedDelRow>) {
        if (!this.get || !event.node.isSelected()) return;

        this.selectedRequisitionDel$.next(event.data?.requisitiondel_keyno ?? null);
    }

    private async loadGet() {
        this.get = await this.requisition.get();
        this.get.selected_requisitiondel_keyno = null;
        this.cdr.markForCheck();
    }

    public readRequisitionDelGrid() {
        if (!this.get) return;

        if (!this.requisitionDelGrid) {
            this.requisitionDelGrid = new Grid<RequisitionSimplifiedDelRow>().setRememberId('w_requisitionSimplifiedRequisitiondelGrid').setLoadDataFunction({ method: 3497, parameters: () => this.get });
        } else {
            this.requisitionDelGrid.gridfunc?.read();
        }

        this.cdr.markForCheck();
    }

    private readRequisitionGrid() {
        if (!this.get) return;

        if (!this.requisitionGrid) {
            this.requisitionGrid = new Grid().setRememberId('w_requisitionSimplifiedRequisitionGrid').setLoadDataFunction({ method: 3498, parameters: () => this.getRequisitionGridParams() });
        } else {
            this.requisitionGrid.gridfunc?.read();
        }

        this.cdr.markForCheck();
    }

    public onRequisitionDelDataChanged(event: RowDataChangeEvent<RequisitionSimplifiedDelRow>) {
        if (!this.get || !event.row) return;

        if (this.sameId(event.row.requisitiondel_keyno, this.get.selected_requisitiondel_keyno)) {
            this.selectedRequisitionDel$.next(event.row.requisitiondel_keyno ?? null);
        }
    }

    private getRequisitionGridParams(): RequisitionSimplifiedGet {
        return {
            ...this.get!,
            selected_requisitiondel_keyno: this.get?.selected_requisitiondel_keyno ?? 0,
        };
    }

    private sameId(first?: string | number | null, second?: string | number | null): boolean {
        return `${first ?? ''}` === `${second ?? ''}`;
    }

    private async init() {
        await this.loadGet();
        this.readRequisitionDelGrid();
    }

    ngOnInit(): void {
        this.navigator?.setSize?.({ width: '95vw', height: '98vh' });
        void this.init();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

type RequisitionSimplifiedDelRow = GridRow & {
    requisitiondel_keyno?: string | number;
    is_selected?: boolean;
};
